import { serialize } from "object-to-formdata";
import request from "../../utils/request";
import { Notification, Result, NotificationType, NotificationsResponseView, OnDemandNotificationInput } from "../../models/general";
import { EmailTaskStatus } from "../../models/deps";


export async function GetNotificationsFirstPagePerUser(userId: string) {
  const response = await request
    .get<Result<NotificationsResponseView>>(`Notification/GetNotificationsFirstPagePerUser/${userId}`);
  return response.data;
}

export async function GetNotificationsPerUser(userId: string, notificationType: number, pageNumber: number, pageSize: number) {
  const response = await request
    .get<Result<NotificationsResponseView>>(`Notification/GetNotificationsPerUser/${userId}/${notificationType?? 0}/${pageNumber?? 1}/${pageSize?? 15}`);
  return response.data;
}

export async function markNotificationAsRead(id: number) {
  const response = await request
    .put<Result<Notification[]>>(`Notification/SetNotificationAsRead/${id}`);
  return response.data;
}

export async function markAllNotificationsAsRead(userId: string) {
  const response = await request
    .put<Result<boolean>>(`Notification/SetAllNotificationsAsRead/${userId}`);
  return response.data;
}

export async function getNotifictionTypes() {
  const response = await request
    .get<Result<NotificationType[]>>(`Notification/Types`);
  return response.data;
}

export async function SendQueuedEmails() {
  const response = await request
    .post<Result<undefined>>(`Notification/SendQueuedEmails`);
  return response.data;
}

export async function SendOnDemandNotification(input : OnDemandNotificationInput) {
  const response = await request
    .post<Result<boolean>>(`Notification/SendOnDemandNotification`, serialize(input));
  return response.data;
}

/**
 * One row of the Operator mail-queue screen (CO-846). Mirrors the server's
 * EmailQueuerTaskListItemView: operational fields only. The wire never carries the recipient
 * address, subject, template model, CC or attachment paths; `toMasked` is first character plus
 * domain and `lastError` is scrubbed of anything address-shaped on the server.
 */
export interface EmailTaskListItem {
  id: number;
  toMasked: string;
  template?: string | null;
  status: EmailTaskStatus;
  retryCount?: number | null;
  createdOn?: string | null;
  sentOn?: string | null;
  nextAttemptOn?: string | null;
  /** Queue priority: below 0 is the bulk tier, 0 and above goes ahead of it (CO-847). */
  priority?: number | null;
  /** Company the mail was queued for; null for platform mail, which has no company (CO-848). */
  companyName?: string | null;
  lastError?: string | null;
  lastErrorOn?: string | null;
  /**
   * CO-959. A finished welcome mail whose starting password was scrubbed (CO-950): the server refuses to
   * re-send it (code `WelcomeRearmBlocked`), and the recovery is HR's "Send welcome email". Absent on an
   * older server, which reads as false.
   */
  rearmBlocked?: boolean | null;
}

/** Server's PagedView<T> envelope. `page` is 1-based. */
export interface EmailTaskPage {
  items: EmailTaskListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface EmailTaskQuery {
  page: number;
  pageSize: number;
  /** Omitted means every status. */
  status?: EmailTaskStatus;
}

export interface EmailQueueStatusCount {
  status: EmailTaskStatus;
  count: number;
}

export interface EmailQueueCompanyWaiting {
  /** null is platform mail (no company). */
  companyName: string | null;
  count: number;
}

/** Wire shape of GET Notification/EmailTasks/Summary. Waiting means Pending, Error or Sending. */
export interface EmailQueueSummary {
  statusCounts: EmailQueueStatusCount[];
  waitingCount: number;
  /** UTC CreatedOn of the oldest waiting row; null when nothing is waiting. */
  oldestWaitingCreatedOn: string | null;
  /** Largest first; the server bounds the list. */
  waitingByCompany: EmailQueueCompanyWaiting[];
}

export async function getEmailTasks(query: EmailTaskQuery) {
  const response = await request
    .get<Result<EmailTaskPage>>(`Notification/EmailTasks`, {
      params: {
        page: query.page,
        pageSize: query.pageSize,
        ...(query.status ? { status: query.status } : {}),
      },
    });
  return response.data;
}

export async function getEmailQueueSummary() {
  const response = await request
    .get<Result<EmailQueueSummary>>(`Notification/EmailTasks/Summary`);
  return response.data;
}

export async function updateEmailTaskStatus(emailTaskId: number, status: EmailTaskStatus) {
  const response = await request
    .put<Result<boolean>>(`Notification/UpdateEmailTaskStatus/${emailTaskId}/${status}`);
  return response.data;
}

